import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submit } from 'redux-form';

import ConfirmationModal from 'components/ModalBox/layouts/ConfirmationModal';
import { codes } from '../../data/translations';
import { TranslationContext } from '../../modules/translations';
import stringUtils from '../../utils/string';

import { DirtyFormProxyReactContext } from './const';
import { submitFailed, submitting } from './selectors';

const DirtyFormProxyConfirmation = ({
	isOpen,
	onClose,
	onDiscard,
	isModalForm,
	hideConfirmButton,
	isSubmitSucceeded,
}) => {
	const dispatch = useDispatch();
	const [isDirtyFormSubmitted, setIsDirtyFormSubmitted] = useState(false);
	const { translate } = useContext(TranslationContext);
	const { subscriber, activeDirtyPageName, activeDirtyFormName, setConfirmationModalOptions } =
		useContext(DirtyFormProxyReactContext);

	const isSubmitting = useSelector(submitting(activeDirtyFormName));
	const isSubmitFailed = useSelector(submitFailed(activeDirtyFormName));

	const handleClose = useCallback(() => {
		if (!isModalForm) {
			subscriber.unregisterUserAction(activeDirtyFormName);
		}

		if (onClose) {
			onClose();
		}

		setConfirmationModalOptions({
			isOpen: false,
			hasDiscard: hideConfirmButton,
		});
	}, [onClose, subscriber, isModalForm, hideConfirmButton, activeDirtyFormName, setConfirmationModalOptions]);

	const handleConfirm = useCallback(() => {
		if (onDiscard) {
			onDiscard();
		} else {
			subscriber.runUserAction(activeDirtyFormName);
		}

		handleClose();
	}, [subscriber, handleClose, onDiscard, activeDirtyFormName]);

	const initActivePageAction = () => {
		subscriber.runPageAction(activeDirtyPageName).then(handleConfirm).catch(handleClose);
	};

	const submitActiveDirtyForm = () => {
		dispatch(submit(activeDirtyFormName));
		setIsDirtyFormSubmitted(true);
	};

	const handleConfirmAndSave = () => {
		if (!stringUtils.isNullOrEmpty(activeDirtyPageName)) {
			initActivePageAction();
		} else {
			submitActiveDirtyForm();
		}
	};

	useEffect(() => {
		if (isDirtyFormSubmitted) {
			if (isSubmitSucceeded && !isSubmitting) {
				setIsDirtyFormSubmitted(false);
				return handleConfirm();
			}

			if (isSubmitFailed && !isSubmitting) {
				setIsDirtyFormSubmitted(false);
				return handleClose();
			}
		}
	}, [handleClose, isSubmitting, handleConfirm, isSubmitFailed, isSubmitSucceeded, isDirtyFormSubmitted]);

	return (
		<ConfirmationModal
			link
			isOpen={isOpen}
			onClose={handleClose}
			onConfirm={handleConfirm}
			isPending={isDirtyFormSubmitted}
			hideConfirmButton={hideConfirmButton}
			onConfirmAndSave={handleConfirmAndSave}
			confirmButtonText={translate(codes.common.discardAndContinue)}
			saveAndConfirmButtonText={translate(codes.common.saveAndContinue)}
			header={translate(codes.timeCards.discardChangesErrorTitle)}
			content={translate(codes.timeCards.discardChangesErrorDescription)}
		/>
	);
};

DirtyFormProxyConfirmation.propTypes = {
	isOpen: PropTypes.bool,
	hideConfirmButton: PropTypes.bool,
	onDiscard: PropTypes.func,
	onClose: PropTypes.func,
	isModalForm: PropTypes.bool,
};

DirtyFormProxyConfirmation.defaultProps = {
	isModalForm: false,
};

export default DirtyFormProxyConfirmation;
